export const LOCAL_DEV_API_BASE_URL = 'http://localhost:8000/api/v1';
export const TEST_API_BASE_URL = 'http://127.0.0.1:9/api/v1';
export const PRODUCTION_API_BASE_URL = 'https://pacilo.nomopark.com/api/v1';
export const KNOWN_PRODUCTION_API_HOSTS = ['pacilo.nomopark.com'];

function readEnv(env, key) {
  if (!env || typeof env !== 'object') {
    return undefined;
  }
  const value = env[key];
  if (value == null) {
    return undefined;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? undefined : trimmed;
}

function isProductionEnv(env) {
  return readEnv(env, 'NODE_ENV') === 'production';
}

function isTruthyEnv(value) {
  return ['1', 'true', 'yes', 'on'].includes(String(value || '').trim().toLowerCase());
}

export function isKnownProductionApiUrl(value) {
  try {
    return KNOWN_PRODUCTION_API_HOSTS.includes(new URL(value).hostname.toLowerCase());
  } catch {
    return false;
  }
}

export function normalizeApiBaseUrl(value) {
  return String(value).trim().replace(/\/+$/, '');
}

function isLoopbackHostname(hostname) {
  const host = String(hostname || '').toLowerCase().replace(/^\[|\]$/g, '');
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.localhost');
}

export function countApiV1(value) {
  const matches = String(value || '').match(/\/api\/v1(?=\/|$)/g);
  return matches ? matches.length : 0;
}

export function isUsableApiBaseUrl(value, { production = false } = {}) {
  if (!value) {
    return false;
  }
  try {
    const parsed = new URL(value);
    if (countApiV1(parsed.pathname) !== 1 && countApiV1(value) !== 1) {
      return false;
    }
    if (production) {
      if (parsed.protocol !== 'https:') {
        return false;
      }
      if (isLoopbackHostname(parsed.hostname)) {
        return false;
      }
    } else if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false;
    }
    return countApiV1(value) === 1;
  } catch {
    return false;
  }
}

export function resolveApiBaseUrl(env = process.env) {
  const configured = readEnv(env, 'REACT_APP_API_BASE_URL');
  const production = isProductionEnv(env);

  if (!configured) {
    if (production || process.env.NODE_ENV === 'production') {
      throw new Error('REACT_APP_API_BASE_URL is required in production and must be an absolute HTTPS URL containing /api/v1 exactly once.');
    }
    return LOCAL_DEV_API_BASE_URL;
  }

  const normalized = normalizeApiBaseUrl(configured);
  if (!isUsableApiBaseUrl(normalized, { production })) {
    if (production) {
      throw new Error('REACT_APP_API_BASE_URL must be an absolute HTTPS URL containing /api/v1 exactly once and must not use a localhost API fallback.');
    }
    throw new Error('REACT_APP_API_BASE_URL is malformed. It must be an absolute URL containing /api/v1 exactly once.');
  }

  if (!production && isKnownProductionApiUrl(normalized) && !isTruthyEnv(readEnv(env, 'REACT_APP_ALLOW_PRODUCTION_API'))) {
    throw new Error('Development and test builds must not silently target the production API. Use the local Laravel origin, or set REACT_APP_ALLOW_PRODUCTION_API=true to override.');
  }

  return normalized;
}

export const API_BASE_URL = resolveApiBaseUrl();

export const endpoints = {
  otpRequest: '/auth/otp/request',
  otpVerify: '/auth/otp/verify',
  profileOptions: '/auth/profile-options',
  refresh: '/auth/refresh',
  me: '/auth/me',
  profile: '/auth/profile',
  logout: '/auth/logout',
  deletionRequest: '/auth/account-deletion/request',
  deletionConfirm: '/auth/account-deletion/confirm',
  legalCurrent: '/legal-documents/current',
  legalAccept: '/legal-documents/accept',
  home: '/professional-assessment/home',
  assessment: '/professional-assessment',
  startAttempt: '/professional-assessment/attempts',
  attempt: (id) => `/professional-assessment/attempts/${id}`,
  saveAnswers: (id) => `/professional-assessment/attempts/${id}/answers`,
  submitAttempt: (id) => `/professional-assessment/attempts/${id}/submit`,
  results: '/professional-assessment/results',
  result: (id) => `/professional-assessment/results/${id}`,
  consultationOptions: '/professional-assessment/consultation-options',
  consultations: '/professional-assessment/consultations',
  consultation: (id) => `/professional-assessment/consultations/${id}`,
  cancelConsultation: (id) => `/professional-assessment/consultations/${id}/cancel`,
  withdrawConsent: (id) => `/professional-assessment/consultations/${id}/withdraw-consent`,
  notifications: '/notifications',
  unreadCount: '/notifications/unread-count',
  markNotificationRead: (id) => `/notifications/${id}/read`,
  markAllNotificationsRead: '/notifications/read-all',
  supportCategories: '/support/categories',
  supportTickets: '/support/tickets',
  supportTicket: (id) => `/support/tickets/${id}`,
  companyAssessmentPlans: '/company-assessment-plans',
  companies: '/companies',
  company: (id) => `/companies/${id}`,
  companyMemberships: (companyId) => `/companies/${companyId}/memberships`,
  companyMembership: (companyId, membershipId) => `/companies/${companyId}/memberships/${membershipId}`,
  companyPurchases: (companyId) => `/companies/${companyId}/purchases`,
  companyPurchase: (companyId, purchaseId) => `/companies/${companyId}/purchases/${purchaseId}`,
  companyPurchaseCheckout: (companyId, purchaseId) => `/companies/${companyId}/purchases/${purchaseId}/checkout`,
  companyEntitlements: (companyId) => `/companies/${companyId}/entitlements`,
  companyEntitlement: (companyId, entitlementId) => `/companies/${companyId}/entitlements/${entitlementId}`,
  companyEntitlementCodes: (companyId, entitlementId) => `/companies/${companyId}/entitlements/${entitlementId}/codes`,
  companyEntitlementCodeBatches: (companyId, entitlementId) => `/companies/${companyId}/entitlements/${entitlementId}/code-batches`,
  companyCodes: (companyId) => `/companies/${companyId}/codes`,
  companyCode: (companyId, codeId) => `/companies/${companyId}/codes/${codeId}`,
  companyCodeRevoke: (companyId, codeId) => `/companies/${companyId}/codes/${codeId}/revoke`,
  companySponsoredParticipants: (companyId) => `/companies/${companyId}/sponsored-participants`,
  companySponsoredParticipant: (companyId, attemptId) => `/companies/${companyId}/sponsored-participants/${attemptId}`,
  assessmentAccessCodeInspect: '/assessment-access-codes/inspect',
  assessmentAccessCodeRedeem: '/assessment-access-codes/redeem',
};
